import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from './AdminLayout';
import { adminGet, adminPut } from './adminApi';

const FIELD_TYPES = ['text', 'list', 'freetext'];

const emptyQuestion = () => ({
  id: `q${Date.now()}`,
  prompt: '',
  field: '',
  type: 'text',
  required: false,
  follow_up: '',
  follow_up_field: '',
  default: '',
  sensitive: false,
});

const styles = `
  .iq-toolbar { display:flex; align-items:center; justify-content:space-between; margin-bottom:24px; flex-wrap:wrap; gap:12px; }
  .iq-save-btn { background:#E8845A; color:#fff; border:none; border-radius:10px; padding:10px 24px; font-size:0.95rem; font-weight:600; cursor:pointer; font-family:inherit; transition:background 0.2s; }
  .iq-save-btn:hover { background:#D4724A; }
  .iq-save-btn:disabled { background:#ccc; cursor:not-allowed; }
  .iq-add-btn { background:#F5F0EB; color:#2C2C2C; border:1.5px solid #E0D8D0; border-radius:10px; padding:10px 20px; font-size:0.9rem; font-weight:600; cursor:pointer; font-family:inherit; transition:all 0.2s; }
  .iq-add-btn:hover { background:#EDE7E0; }
  .iq-list { display:flex; flex-direction:column; gap:16px; }
  .iq-card { background:#fff; border:1.5px solid #E8E3DC; border-radius:14px; overflow:hidden; }
  .iq-card-header { display:flex; align-items:center; gap:12px; padding:14px 18px; background:#FAFAF8; border-bottom:1px solid #EEE8E0; cursor:pointer; user-select:none; }
  .iq-card-header:hover { background:#F5F0EB; }
  .iq-drag-handle { color:#bbb; font-size:1.1rem; cursor:grab; }
  .iq-q-number { font-size:0.78rem; font-weight:700; color:#aaa; text-transform:uppercase; letter-spacing:0.05em; min-width:28px; }
  .iq-q-prompt { flex:1; font-size:0.95rem; font-weight:500; color:#2C2C2C; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
  .iq-q-prompt.empty { color:#bbb; font-style:italic; }
  .iq-badges { display:flex; gap:6px; flex-shrink:0; }
  .iq-badge { font-size:0.72rem; font-weight:600; padding:3px 8px; border-radius:20px; }
  .iq-badge-required { background:#FFEDE3; color:#C06030; }
  .iq-badge-optional { background:#F0F0F0; color:#888; }
  .iq-badge-sensitive { background:#FFF3CD; color:#856404; }
  .iq-expand-icon { color:#bbb; font-size:0.9rem; transition:transform 0.2s; }
  .iq-expand-icon.open { transform:rotate(180deg); }
  .iq-card-body { padding:18px; display:flex; flex-direction:column; gap:14px; }
  .iq-row { display:flex; gap:12px; flex-wrap:wrap; }
  .iq-field { display:flex; flex-direction:column; gap:6px; flex:1; min-width:160px; }
  .iq-field label { font-size:0.78rem; font-weight:600; color:#888; text-transform:uppercase; letter-spacing:0.04em; }
  .iq-field input, .iq-field select, .iq-field textarea {
    border:1.5px solid #E0D8D0; border-radius:8px; padding:9px 12px;
    font-size:0.9rem; font-family:inherit; color:#2C2C2C; background:#FAFAF8;
    outline:none; transition:border-color 0.2s;
  }
  .iq-field input:focus, .iq-field select:focus, .iq-field textarea:focus { border-color:#E8845A; background:#fff; }
  .iq-field textarea { resize:vertical; min-height:64px; }
  .iq-checkrow { display:flex; gap:20px; align-items:center; flex-wrap:wrap; }
  .iq-check { display:flex; align-items:center; gap:7px; font-size:0.88rem; color:#555; cursor:pointer; }
  .iq-check input[type=checkbox] { width:16px; height:16px; cursor:pointer; accent-color:#E8845A; }
  .iq-card-footer { display:flex; justify-content:flex-end; padding:10px 18px; border-top:1px solid #F0EBE5; }
  .iq-delete-btn { background:none; border:none; color:#DC3545; font-size:0.85rem; font-weight:600; cursor:pointer; padding:6px 12px; border-radius:8px; font-family:inherit; }
  .iq-delete-btn:hover { background:#FFF0F0; }
  .iq-reorder { display:flex; gap:4px; }
  .iq-reorder-btn { background:none; border:1px solid #E0D8D0; color:#888; border-radius:6px; width:28px; height:28px; font-size:0.9rem; cursor:pointer; display:flex; align-items:center; justify-content:center; }
  .iq-reorder-btn:hover { background:#F5F0EB; color:#2C2C2C; }
  .iq-reorder-btn:disabled { opacity:0.3; cursor:not-allowed; }
  .iq-toast { position:fixed; bottom:24px; right:24px; background:#28A745; color:#fff; padding:12px 24px; border-radius:12px; font-weight:600; font-size:0.9rem; box-shadow:0 4px 20px rgba(0,0,0,0.15); z-index:9999; animation:fadeInUp 0.3s ease; }
  .iq-toast.error { background:#DC3545; }
  @keyframes fadeInUp { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
`;

export default function AdminIntakeQuestions() {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [toast, setToast] = useState(null);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    adminGet('/intake-questions')
      .then(data => { setQuestions(data.questions || []); setLoading(false); })
      .catch(() => { setToast({ msg: 'Failed to load questions', error: true }); setLoading(false); });
  }, []);

  const showToast = (msg, error = false) => {
    setToast({ msg, error });
    setTimeout(() => setToast(null), 3000);
  };

  const update = useCallback((idx, key, value) => {
    setQuestions(prev => prev.map((q, i) => i === idx ? { ...q, [key]: value } : q));
    setDirty(true);
  }, []);

  const addQuestion = () => {
    const q = emptyQuestion();
    setQuestions(prev => [...prev, q]);
    setExpanded(prev => ({ ...prev, [q.id]: true }));
    setDirty(true);
  };

  const deleteQuestion = (idx) => {
    if (!window.confirm('Delete this question?')) return;
    setQuestions(prev => prev.filter((_, i) => i !== idx));
    setDirty(true);
  };

  const moveUp = (idx) => {
    if (idx === 0) return;
    setQuestions(prev => {
      const a = [...prev];
      [a[idx - 1], a[idx]] = [a[idx], a[idx - 1]];
      return a;
    });
    setDirty(true);
  };

  const moveDown = (idx) => {
    setQuestions(prev => {
      if (idx >= prev.length - 1) return prev;
      const a = [...prev];
      [a[idx], a[idx + 1]] = [a[idx + 1], a[idx]];
      return a;
    });
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await adminPut('/intake-questions', { questions });
      setDirty(false);
      showToast('Questions saved ✓');
    } catch (e) {
      showToast(e.message || 'Save failed', true);
    } finally {
      setSaving(false);
    }
  };

  const toggleExpand = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  if (loading) return <AdminLayout title="Intake Questions"><p style={{padding:'32px',color:'#888'}}>Loading…</p></AdminLayout>;

  return (
    <AdminLayout title="Intake Questions">
      <style>{styles}</style>

      <div className="iq-toolbar">
        <div style={{display:'flex',gap:'10px'}}>
          <button className="iq-add-btn" onClick={addQuestion}>+ Add question</button>
        </div>
        <button className="iq-save-btn" onClick={save} disabled={saving || !dirty}>
          {saving ? 'Saving…' : dirty ? 'Save changes' : 'Saved'}
        </button>
      </div>

      <div className="iq-list">
        {questions.map((q, idx) => {
          const isOpen = !!expanded[q.id];
          return (
            <div key={q.id} className="iq-card">
              <div className="iq-card-header" onClick={() => toggleExpand(q.id)}>
                <span className="iq-q-number">Q{idx + 1}</span>
                <span className={`iq-q-prompt${!q.prompt ? ' empty' : ''}`}>
                  {q.prompt || 'New question…'}
                </span>
                <div className="iq-badges">
                  {q.sensitive && <span className="iq-badge iq-badge-sensitive">sensitive</span>}
                  <span className={`iq-badge ${q.required ? 'iq-badge-required' : 'iq-badge-optional'}`}>
                    {q.required ? 'required' : 'optional'}
                  </span>
                </div>
                <div className="iq-reorder" onClick={e => e.stopPropagation()}>
                  <button className="iq-reorder-btn" onClick={() => moveUp(idx)} disabled={idx === 0} title="Move up">↑</button>
                  <button className="iq-reorder-btn" onClick={() => moveDown(idx)} disabled={idx === questions.length - 1} title="Move down">↓</button>
                </div>
                <span className={`iq-expand-icon${isOpen ? ' open' : ''}`}>▼</span>
              </div>

              {isOpen && (
                <div className="iq-card-body">
                  <div className="iq-row">
                    <div className="iq-field" style={{flex:3}}>
                      <label>Prompt (what Mia asks)</label>
                      <textarea
                        value={q.prompt || ''}
                        onChange={e => update(idx, 'prompt', e.target.value)}
                        placeholder="What's their first name?"
                      />
                    </div>
                  </div>
                  <div className="iq-row">
                    <div className="iq-field">
                      <label>Field name (internal key)</label>
                      <input value={q.field || ''} onChange={e => update(idx, 'field', e.target.value)} placeholder="first_name" />
                    </div>
                    <div className="iq-field">
                      <label>ID</label>
                      <input value={q.id || ''} onChange={e => update(idx, 'id', e.target.value)} placeholder="q1" />
                    </div>
                    <div className="iq-field">
                      <label>Type</label>
                      <select value={q.type || 'text'} onChange={e => update(idx, 'type', e.target.value)}>
                        {FIELD_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="iq-row">
                    <div className="iq-field">
                      <label>Follow-up prompt (optional)</label>
                      <input value={q.follow_up || ''} onChange={e => update(idx, 'follow_up', e.target.value)} placeholder="And their last name?" />
                    </div>
                    <div className="iq-field">
                      <label>Follow-up field</label>
                      <input value={q.follow_up_field || ''} onChange={e => update(idx, 'follow_up_field', e.target.value)} placeholder="last_name" />
                    </div>
                  </div>
                  <div className="iq-row">
                    <div className="iq-field">
                      <label>Default value (optional)</label>
                      <input value={q.default || ''} onChange={e => update(idx, 'default', e.target.value)} placeholder="Lena" />
                    </div>
                  </div>
                  <div className="iq-checkrow">
                    <label className="iq-check">
                      <input type="checkbox" checked={!!q.required} onChange={e => update(idx, 'required', e.target.checked)} />
                      Required
                    </label>
                    <label className="iq-check">
                      <input type="checkbox" checked={!!q.sensitive} onChange={e => update(idx, 'sensitive', e.target.checked)} />
                      Sensitive (not shown to family)
                    </label>
                  </div>
                  <div className="iq-card-footer">
                    <button className="iq-delete-btn" onClick={() => deleteQuestion(idx)}>🗑 Delete question</button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {questions.length === 0 && (
        <div style={{textAlign:'center',padding:'48px',color:'#aaa'}}>
          <div style={{fontSize:'2rem',marginBottom:'12px'}}>📋</div>
          <div>No questions yet. Add one to get started.</div>
        </div>
      )}

      {toast && (
        <div className={`iq-toast${toast.error ? ' error' : ''}`}>{toast.msg}</div>
      )}
    </AdminLayout>
  );
}
